import React, { useState } from 'react';
import { FiChevronDown, FiChevronUp, FiMessageCircle } from 'react-icons/fi';
import { getAssistantContext } from '../utils/assistantContexts';
import PlanUpgradeCard from './PlanUpgradeCard';

/**
 * Asistente compacto por pantalla (POST /api/asistente/chat).
 */
export default function ContextualAssistantStrip({
  contextId = 'inicio-semana',
  secureFetch,
  hasProfesional,
  modoOscuro,
  textSec,
  textMuted,
  defaultExpanded = false,
}) {
  const ctx = getAssistantContext(contextId);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [lastReply, setLastReply] = useState('');
  const [error, setError] = useState('');

  const box = modoOscuro ? 'bg-gray-900/60 border-gray-700' : 'bg-[#1F80FF]/5 border-[#1F80FF]/20';
  const chip = modoOscuro
    ? 'bg-gray-800 text-gray-200 hover:bg-gray-700'
    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200';

  if (!hasProfesional) {
    return (
      <PlanUpgradeCard
        compact
        description={`Asistente en ${ctx.label}: preguntas frecuentes con plan Profesional+.`}
        planLabel="Profesional"
        modoOscuro={modoOscuro}
      />
    );
  }

  const ask = async (text) => {
    const message = String(text || '').trim();
    if (!message || asking) return;
    setAsking(true);
    setError('');
    setLastReply('');
    try {
      const res = await secureFetch('/api/asistente/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'No pudimos responder');
      setLastReply(json.reply || '');
      setQuestion('');
    } catch (err) {
      setError(err.message || 'Error al consultar');
    } finally {
      setAsking(false);
    }
  };

  return (
    <div className={`rounded-xl border ${box}`}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left"
        aria-expanded={expanded}
      >
        <span className="inline-flex items-center gap-2 min-w-0">
          <FiMessageCircle className="w-4 h-4 shrink-0 text-[#1F80FF]" />
          <span className={`text-xs font-semibold ${modoOscuro ? 'text-white' : 'text-gray-900'}`}>
            Asistente · {ctx.label}
          </span>
          {!expanded && (
            <span className={`text-[10px] truncate hidden sm:inline ${textMuted}`}>{ctx.intro}</span>
          )}
        </span>
        {expanded ? (
          <FiChevronUp className="w-4 h-4 shrink-0 opacity-60" />
        ) : (
          <FiChevronDown className="w-4 h-4 shrink-0 opacity-60" />
        )}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-inherit">
          <p className={`text-[11px] pt-2 ${textSec}`}>{ctx.intro}</p>
          <div className="flex flex-wrap gap-1.5">
            {ctx.suggestions.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                disabled={asking}
                onClick={() => ask(suggestion)}
                className={`px-2 py-1 rounded-lg text-[10px] font-medium disabled:opacity-50 ${chip}`}
              >
                {suggestion}
              </button>
            ))}
          </div>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              ask(question);
            }}
          >
            <input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              maxLength={500}
              placeholder="Otra pregunta…"
              className={`flex-1 min-w-0 rounded-lg border px-2.5 py-1.5 text-xs ${
                modoOscuro
                  ? 'bg-gray-900 border-gray-700 text-white placeholder:text-gray-500'
                  : 'bg-white border-gray-200 placeholder:text-gray-400'
              }`}
            />
            <button
              type="submit"
              disabled={asking || !question.trim()}
              className="shrink-0 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-[#1F80FF] text-white disabled:opacity-50"
            >
              {asking ? '…' : 'Preguntar'}
            </button>
          </form>
          {error && <p className="text-[11px] text-red-600">{error}</p>}
          {lastReply && (
            <p className={`text-[11px] leading-relaxed rounded-lg px-2.5 py-2 ${
              modoOscuro ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-700'
            }`}>
              {lastReply}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
